import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Star, MessageSquare, TrendingUp, Send, Award } from 'lucide-react';

const Reviews = () => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    feedback: '',
    transformation_results: '',
    what_you_like: '',
    improvements: '',
    feature_request: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchStats();
    fetchFeaturedReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchFeaturedReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews?featured=true`);
      setFeaturedReviews(response.data.reviews);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/reviews/create`, formData, { headers });
      toast.success('Thank you for your feedback!');
      setShowForm(false);
      setFormData({ rating: 5, feedback: '', transformation_results: '', what_you_like: '', improvements: '', feature_request: '' });
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onSelect && onSelect(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            disabled={!interactive}
          >
            <Star
              size={interactive ? 28 : 16}
              className={star <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#27272A]'}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout title="Reviews & Success Stories">
      <div className="max-w-6xl mx-auto px-4 sm:px-0">
        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <div className="card-gold p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Star className="text-[#D4AF37] fill-[#D4AF37]" size={24} />
              <span className="text-3xl sm:text-4xl text-white font-medium tabular-nums">{stats?.average_rating?.toFixed(1) || '0.0'}</span>
            </div>
            <p className="text-[#A1A1AA] text-sm">Note moyenne</p>
          </div>
          <div className="card-premium p-4 sm:p-6 text-center">
            <span className="text-3xl sm:text-4xl text-[#E5C585] font-medium tabular-nums">{stats?.total_reviews || 0}</span>
            <p className="text-[#A1A1AA] text-sm mt-2">Avis au total</p>
          </div>
          <div className="card-premium p-4 sm:p-6 text-center flex items-center justify-center">
            <Button onClick={() => setShowForm(true)} className="btn-gold px-6 py-3 w-full" data-testid="write-review-btn">
              <MessageSquare size={18} className="mr-2" />
              Écrire un avis
            </Button>
          </div>
        </motion.div>

        {/* Featured Reviews */}
        {featuredReviews.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8 sm:mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Award className="text-[#D4AF37]" size={24} />
              <h2 className="font-serif text-xl sm:text-2xl text-white">Avis mis en avant</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {featuredReviews.slice(0, 4).map((review, index) => (
                <div key={review.review_id} className="card-luxury p-4 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {review.user_picture ? (
                      <img src={review.user_picture} alt={review.user_name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1C1C1C] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#E5C585] font-serif">{review.user_name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1">
                        <span className="text-white font-medium text-sm sm:text-base">{review.user_name}</span>
                        {renderStars(review.rating)}
                      </div>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed break-words">{review.feedback}</p>
                      {review.transformation_results && (
                        <div className="mt-3 sm:mt-4 p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded">
                          <span className="text-[#D4AF37] text-xs font-accent tracking-wider">TRANSFORMATION</span>
                          <p className="text-[#A1A1AA] text-sm mt-1 break-words">{review.transformation_results}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Review Form */}
        {showForm && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="card-luxury p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="font-serif text-xl sm:text-2xl text-white mb-6">Partage ton expérience</h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-3 block">Ta note</label>
                  {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Ton avis *</label>
                  <Textarea
                    value={formData.feedback}
                    onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                    placeholder="Partage ton expérience avec Clierxter Vision Lab..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[100px] text-white"
                    required
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Résultats de ta transformation (Optionnel)</label>
                  <Textarea
                    value={formData.transformation_results}
                    onChange={(e) => setFormData({ ...formData, transformation_results: e.target.value })}
                    placeholder="Partage comment ta marque a évolué..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[80px] text-white"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Ce que tu aimes sur la plateforme</label>
                  <Textarea
                    value={formData.what_you_like}
                    onChange={(e) => setFormData({ ...formData, what_you_like: e.target.value })}
                    placeholder="Les fonctionnalités que tu apprécies..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[60px] text-white"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Ce qu'on pourrait améliorer</label>
                  <Textarea
                    value={formData.improvements}
                    onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
                    placeholder="Tes suggestions..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[60px] text-white"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Quelle fonctionnalité aimerais-tu voir ensuite ?</label>
                  <Textarea
                    value={formData.feature_request}
                    onChange={(e) => setFormData({ ...formData, feature_request: e.target.value })}
                    placeholder="Tes idées de fonctionnalités..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[60px] text-white"
                  />
                </div>

                <div className="flex gap-4 pt-2">
                  <Button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold px-6 py-3">
                    Annuler
                  </Button>
                  <Button type="submit" disabled={loading} className="btn-gold px-6 py-3 flex-1">
                    {loading ? 'Envoi en cours...' : 'Envoyer mon avis'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        {/* All Reviews */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="pb-8">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="text-[#E5C585]" size={24} />
            <h2 className="font-serif text-xl sm:text-2xl text-white">Tous les avis</h2>
            {reviews.length > 0 && (
              <span className="text-[#71717A] text-sm">({reviews.length})</span>
            )}
          </div>
          
          {reviews.length === 0 ? (
            <div className="card-premium p-12 text-center">
              <MessageSquare className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
              <h3 className="text-white font-medium mb-2">Aucun avis pour le moment</h3>
              <p className="text-[#71717A] text-sm mb-6">Sois la première à partager ton expérience</p>
              <Button onClick={() => setShowForm(true)} className="btn-gold px-6 py-3">
                <MessageSquare size={18} className="mr-2" />
                Écrire un avis
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="card-premium p-4 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {review.user_picture ? (
                      <img src={review.user_picture} alt={review.user_name} className="w-10 h-10 rounded-full flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#E5C585] font-serif text-sm">{review.user_name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1 sm:gap-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="text-white text-sm sm:text-base">{review.user_name}</span>
                          <span className="text-[#52525B] text-xs">{new Date(review.created_at).toLocaleDateString()}</span>
                        </div>
                        {renderStars(review.rating)}
                      </div>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed break-words">{review.feedback}</p>
                      {review.transformation_results && (
                        <div className="mt-3 p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded">
                          <span className="text-[#D4AF37] text-xs font-accent tracking-wider">TRANSFORMATION</span>
                          <p className="text-[#A1A1AA] text-sm mt-1">{review.transformation_results}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Reviews;
